import {
  clampScaleToBandableExportWidth,
  isCanvasWithinExportPixelBudget,
  isExportPixelBudgetExceeded,
  PNG_EXPORT_MAX_PIXELS,
  resolveExportCaptureBandCssHeight,
  resolveExportCaptureBandDeviceHeight,
  resolveHtml2CanvasScale,
} from './exportCaptureScale.js';
import { freezeExportCaptureMotion, waitForExportPreflight } from './freezeExportCapture.js';
import { sanitizeClonedExportColors } from './sanitizeClonedExportColors.js';
import { FullPageExportStageError } from './types.js';

const CLIPPING_OVERFLOW = /^(auto|scroll|hidden)$/i;
const CLIPPING_OVERFLOW_CLASS = /\boverflow(?:-[xy])?-(?:auto|scroll|hidden)\b/;
const CLIPPING_MAX_HEIGHT_CLASS = /\bmax-h-/;

export interface ExportCaptureCssBox {
  readonly width: number;
  readonly height: number;
}

/** Cross-realm safe (html2canvas / jsdom iframe clones fail parent-realm instanceof). */
function isStyledHtmlElement(
  node: Node | null | undefined,
): node is HTMLElement {
  if (node == null || node.nodeType !== 1) {
    return false;
  }
  const el = node as Element & { style?: CSSStyleDeclaration; classList?: DOMTokenList };
  return el.style != null && typeof el.style.setProperty === 'function' && el.classList != null;
}

function isExportExcludedChrome(el: Element): boolean {
  if (!isStyledHtmlElement(el)) {
    return false;
  }
  return (
    el.getAttribute('data-export-exclude') === 'chrome' ||
    el.closest('[data-export-exclude="chrome"]') != null
  );
}

function readClassName(el: Element): string {
  const className = (el as HTMLElement).className;
  if (typeof className === 'string') {
    return className;
  }
  return el.getAttribute('class') ?? '';
}

function unclipOverflow(node: HTMLElement): void {
  const { style } = node;
  style.overflow = 'visible';
  style.overflowY = 'visible';
  style.overflowX = 'visible';
  style.maxHeight = 'none';
}

function readsAsClippingOverflow(el: HTMLElement): boolean {
  if (
    CLIPPING_OVERFLOW.test(el.style.overflow) ||
    CLIPPING_OVERFLOW.test(el.style.overflowY) ||
    CLIPPING_OVERFLOW.test(el.style.overflowX)
  ) {
    return true;
  }
  if (CLIPPING_OVERFLOW_CLASS.test(readClassName(el))) {
    return true;
  }
  const computed = el.ownerDocument.defaultView?.getComputedStyle(el);
  if (computed) {
    if (
      CLIPPING_OVERFLOW.test(computed.overflowY) ||
      CLIPPING_OVERFLOW.test(computed.overflowX) ||
      CLIPPING_OVERFLOW.test(computed.overflow)
    ) {
      return true;
    }
  }
  return false;
}

function readsAsClippingMaxHeight(el: HTMLElement): boolean {
  const inlineMax = el.style.maxHeight;
  if (inlineMax !== '' && inlineMax !== 'none') {
    return true;
  }
  return CLIPPING_MAX_HEIGHT_CLASS.test(readClassName(el));
}

function throwCaptureStageError(err: unknown, fallbackMessage: string): never {
  if (err instanceof FullPageExportStageError) {
    throw err;
  }
  const message = err instanceof Error && err.message !== '' ? err.message : fallbackMessage;
  throw new FullPageExportStageError('capture', message, { cause: err });
}

/**
 * Resolve the cloned capture root. Never depend only on `getElementById` of an empty live id.
 * Order: html2canvas `clonedElement` → `[data-admin-export-surface="page"]` → `#admin-export-surface`.
 */
export function resolveClonedExportTarget(
  clonedDocument: Document,
  clonedElement: HTMLElement | undefined,
  liveElement: HTMLElement,
): HTMLElement | null {
  if (isStyledHtmlElement(clonedElement)) {
    return clonedElement;
  }
  const bySurface = clonedDocument.querySelector('[data-admin-export-surface="page"]');
  if (isStyledHtmlElement(bySurface)) {
    return bySurface;
  }
  const byStableId = clonedDocument.getElementById('admin-export-surface');
  if (isStyledHtmlElement(byStableId)) {
    return byStableId;
  }
  if (liveElement.id !== '') {
    const byLiveId = clonedDocument.getElementById(liveElement.id);
    if (isStyledHtmlElement(byLiveId)) {
      return byLiveId;
    }
  }
  return null;
}

/**
 * Unclip overflow on the cloned export tree only (never the live document).
 * G5 light-force should run after this in `onclone` — do not fold light-forcing into this helper.
 */
export function unclipClonedExportTree(
  clonedRoot: Document,
  liveElement: HTMLElement,
  cssBox: ExportCaptureCssBox,
  clonedElement?: HTMLElement,
): HTMLElement | null {
  const target = resolveClonedExportTarget(clonedRoot, clonedElement, liveElement);
  if (!isStyledHtmlElement(target) || target.ownerDocument !== clonedRoot) {
    return null;
  }

  target.style.height = `${cssBox.height}px`;
  target.style.maxHeight = 'none';
  target.style.overflow = 'visible';
  target.style.overflowY = 'visible';

  let ancestor: Element | null = target.parentElement;
  while (ancestor != null) {
    if (isStyledHtmlElement(ancestor) && (readsAsClippingOverflow(ancestor) || ancestor.id === 'main')) {
      unclipOverflow(ancestor);
    }
    if (ancestor === clonedRoot.documentElement) {
      break;
    }
    ancestor = ancestor.parentElement;
  }

  const descendants = target.querySelectorAll('*');
  descendants.forEach((node) => {
    if (!isStyledHtmlElement(node)) {
      return;
    }
    if (readsAsClippingOverflow(node) || readsAsClippingMaxHeight(node)) {
      unclipOverflow(node);
    }
  });

  return target;
}

/** Clone-only light export (AC-11). Never mutate the live document. */
export function forceClonedExportLight(clonedRoot: Document, captureRoot: HTMLElement | null): void {
  const paintLight = (node: Element): void => {
    if (!isStyledHtmlElement(node)) {
      return;
    }
    node.classList.remove('dark');
    const { style } = node;
    style.colorScheme = 'light';
    style.backgroundColor = '#ffffff';
    style.color = '#111111';
  };

  paintLight(clonedRoot.documentElement);
  if (clonedRoot.body != null) {
    paintLight(clonedRoot.body);
  }
  if (captureRoot != null) {
    paintLight(captureRoot);
  }
}

/** CSS box used for scale + html2canvas window/height so capture is the full element, not the viewport clip. */
export function resolveExportCaptureCssBox(element: HTMLElement): ExportCaptureCssBox {
  const rect = element.getBoundingClientRect();
  const width = Math.max(1, element.scrollWidth, element.offsetWidth, Math.round(rect.width));
  const height = Math.max(1, element.scrollHeight, element.offsetHeight, Math.round(rect.height));
  return { width, height };
}

interface Html2CanvasCaptureOptions {
  readonly backgroundColor: string;
  readonly scale: number;
  readonly width: number;
  readonly height: number;
  readonly windowWidth: number;
  readonly windowHeight: number;
  readonly scrollX: number;
  readonly scrollY: number;
  readonly x?: number;
  readonly y?: number;
  readonly ignoreElements: (el: Element) => boolean;
  readonly onclone: (clonedDocument: Document, clonedElement?: HTMLElement) => void;
}

function buildCaptureOptions(
  cssBox: ExportCaptureCssBox,
  scale: number,
  crop: { readonly y: number; readonly height: number } | null,
  onclone: Html2CanvasCaptureOptions['onclone'],
): Html2CanvasCaptureOptions {
  return {
    backgroundColor: '#ffffff',
    scale,
    width: cssBox.width,
    height: crop?.height ?? cssBox.height,
    windowWidth: cssBox.width,
    windowHeight: cssBox.height,
    scrollX: 0,
    scrollY: 0,
    ...(crop != null ? { x: 0, y: crop.y } : {}),
    ignoreElements: isExportExcludedChrome,
    onclone,
  };
}

/**
 * Split an over-budget raster into vertical bands that each stay ≤ PNG_EXPORT_MAX_PIXELS.
 * Used when html2canvas returns a larger canvas than planned (WebKit overrun hardening).
 * Defensive post-capture split — not a proven live Safari/device root-cause claim.
 */
function splitCanvasToExportPixelBudget(canvas: HTMLCanvasElement): HTMLCanvasElement[] {
  if (isCanvasWithinExportPixelBudget(canvas)) {
    return [canvas];
  }

  if (canvas.width > PNG_EXPORT_MAX_PIXELS) {
    throw new FullPageExportStageError(
      'capture',
      `Export capture is too wide for the mobile canvas budget (${canvas.width}px). Try a narrower layout, or export again after rotating to portrait.`,
    );
  }

  const bandHeight = resolveExportCaptureBandDeviceHeight(canvas.width);
  if (canvas.width * bandHeight > PNG_EXPORT_MAX_PIXELS) {
    throw new FullPageExportStageError(
      'capture',
      `Export capture exceeded PNG_EXPORT_MAX_PIXELS (${PNG_EXPORT_MAX_PIXELS}). Retry the export, or try PDF.`,
    );
  }

  const bands: HTMLCanvasElement[] = [];
  const doc = canvas.ownerDocument ?? document;
  for (let y = 0; y < canvas.height; y += bandHeight) {
    const height = Math.min(bandHeight, canvas.height - y);
    const band = doc.createElement('canvas');
    band.width = canvas.width;
    band.height = height;
    const ctx = band.getContext('2d');
    if (ctx == null) {
      throw new FullPageExportStageError(
        'capture',
        'Export capture failed: 2D canvas context is unavailable. Retry the export.',
      );
    }
    ctx.drawImage(canvas, 0, y, canvas.width, height, 0, 0, canvas.width, height);
    if (!isCanvasWithinExportPixelBudget(band)) {
      throw new FullPageExportStageError(
        'capture',
        `Export capture band exceeded PNG_EXPORT_MAX_PIXELS (${PNG_EXPORT_MAX_PIXELS}). Retry the export, or try PDF.`,
      );
    }
    bands.push(band);
  }
  return bands;
}

function enforceReturnedCanvasesWithinBudget(
  canvases: readonly HTMLCanvasElement[],
): HTMLCanvasElement[] {
  const enforced: HTMLCanvasElement[] = [];
  for (const canvas of canvases) {
    enforced.push(...splitCanvasToExportPixelBudget(canvas));
  }
  return enforced;
}

/**
 * Rasterize `element` via dynamic `html2canvas` (never static).
 * Returns one canvas when in budget, otherwise vertical bands that each stay ≤ PNG_EXPORT_MAX_PIXELS.
 * Freezes motion unless the caller already froze the same root (exportFullPage).
 * Failures are tagged with {@link FullPageExportStageError} stage `'capture'`.
 */
export async function captureElement(element: HTMLElement): Promise<HTMLCanvasElement[]> {
  const ownsFreeze = element.getAttribute('data-export-capture') !== 'true';
  const restoreMotion = ownsFreeze ? freezeExportCaptureMotion(element) : () => undefined;

  try {
    try {
      await waitForExportPreflight(element);

      const cssBox = resolveExportCaptureCssBox(element);
      const preferredScale = resolveHtml2CanvasScale({
        cssWidth: cssBox.width,
        cssHeight: cssBox.height,
      });
      const scale = clampScaleToBandableExportWidth(cssBox.width, preferredScale);

      if (isExportPixelBudgetExceeded(cssBox.width, 1, scale)) {
        throw new FullPageExportStageError(
          'capture',
          `Export capture is too wide for the mobile canvas budget (${cssBox.width} CSS px at scale ${scale}). Try a narrower layout, or export again after rotating to portrait.`,
        );
      }

      const onclone = (clonedDocument: Document, clonedElement?: HTMLElement): void => {
        const target = unclipClonedExportTree(clonedDocument, element, cssBox, clonedElement);
        forceClonedExportLight(clonedDocument, target);
        // html2canvas 1.4 cannot parse color(srgb…)/oklch from color-mix computed styles.
        sanitizeClonedExportColors(clonedDocument, target);
      };

      const html2canvas = await import('html2canvas');
      const canvases: HTMLCanvasElement[] = [];

      if (!isExportPixelBudgetExceeded(cssBox.width, cssBox.height, scale)) {
        canvases.push(
          await html2canvas.default(element, buildCaptureOptions(cssBox, scale, null, onclone)),
        );
        return enforceReturnedCanvasesWithinBudget(canvases);
      }

      const bandCssHeight = resolveExportCaptureBandCssHeight(cssBox.width, scale);
      if (isExportPixelBudgetExceeded(cssBox.width, bandCssHeight, scale)) {
        throw new FullPageExportStageError(
          'capture',
          `Export capture could not fit within PNG_EXPORT_MAX_PIXELS (${PNG_EXPORT_MAX_PIXELS}) even when banded. Retry the export, or try PDF.`,
        );
      }

      let y = 0;
      while (y < cssBox.height) {
        const height = Math.min(bandCssHeight, cssBox.height - y);
        if (isExportPixelBudgetExceeded(cssBox.width, height, scale)) {
          throw new FullPageExportStageError(
            'capture',
            `Export capture band would exceed PNG_EXPORT_MAX_PIXELS (${PNG_EXPORT_MAX_PIXELS}). Retry the export, or try PDF.`,
          );
        }
        canvases.push(
          await html2canvas.default(element, buildCaptureOptions(cssBox, scale, { y, height }, onclone)),
        );
        y += height;
      }

      return enforceReturnedCanvasesWithinBudget(canvases);
    } catch (err) {
      throwCaptureStageError(
        err,
        'Export capture failed. Retry the export, or try a different format.',
      );
    }
  } finally {
    restoreMotion();
  }
}
